import React, { useState } from 'react';
import {
  BooleanInput,
  Button,
  FormWithRedirect,
  required,
  SaveButton,
  SelectInput,
  TextInput,
  useNotify,
} from 'react-admin';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@material-ui/core/';
import IconCancel from '@material-ui/icons/Cancel';
import PeopleIcon from '@material-ui/icons/People';
import SendIcon from '@material-ui/icons/Send';
import { availableMessageTemplates, serverApiUrl } from '../../../config/constants';
import httpClient from '../../../core/httpClient';

import styles from './SendToGroupOfUsers.module.scss';
import { CustomDateTimeInput } from '../../CustomDateTimeInput';

const SendToGroupOfUsersWithSchedule = ({ endpointUrl, recordKey, record }) => {
  const notify = useNotify();
  const [isOpen, setOpenStatus] = useState(false);
  const [targetGroupsList, setTargetGroupsList] = useState([]);
  const [isInnerOpen, setInnerOpenStatus] = useState(false);

  const handleOpenModal = (e) => {
    e.stopPropagation();
    const url = `${serverApiUrl}/admin/target-groups?limit=100`;
    const options = { method: 'GET' };

    httpClient(url, options)
      .then((res) => {
        setTargetGroupsList(res.json['target-groups']);
        setOpenStatus(true);
      })
      .catch(() => {
        notify("Error: Can't get target groups list", 'warning');
      });
  };

  const handleCloseModal = () => {
    setOpenStatus(false);
  };

  const handleSendMessage = ({ message, target_group_id, caption, send_push_notification }) => {
    const url = `${serverApiUrl}${endpointUrl}`;
    const body = JSON.stringify({ message, caption, target_group_id, send_push_notification, [recordKey]: record.id });
    const options = { method: 'POST', body };

    httpClient(url, options)
      .then(() => handleCloseModal())
      .catch(() => notify("Error: Message wasn't sent", 'warning'));
  };

  const handleOpenInnerModal = (e) => {
    e.stopPropagation();
    setInnerOpenStatus(true);
  };

  const handleCloseInnerModal = () => {
    setInnerOpenStatus(false);
  };

  const handleSchedule = async ({ message, target_group_id, caption, schedule_date, send_push_notification }) => {
    if (!schedule_date || !target_group_id || !message) {
      return notify('Field is missing', 'warning');
    }

    const url = `${serverApiUrl}/admin/schedule/article`;
    const body = JSON.stringify({
      message,
      caption,
      target_group_id,
      send_push_notification,
      schedule_date: schedule_date ? new Date(schedule_date).toISOString() : null,
      [recordKey]: record.id,
      type: 'group',
    });
    const options = { method: 'POST', body };

    httpClient(url, options)
      .then(() => {
        notify('Message was scheduled', 'info');
        handleCloseModal();
        handleCloseInnerModal();
      })
      .catch(() => notify("Error: Message wasn't scheduled", 'warning'));
  };

  return (
    <>
      <IconButton onClick={handleOpenModal}>
        <PeopleIcon />
      </IconButton>
      <Dialog open={isOpen} onClick={(e) => e.stopPropagation()} maxWidth="xs">
        <DialogTitle>Send to target group</DialogTitle>
        <FormWithRedirect
          save={handleSendMessage}
          render={({ handleSubmitWithRedirect, submitting, pristine, form }) => (
            <>
              <DialogContent dividers className={styles.dialogContent}>
                <TextInput label="Caption" source="caption" />
                <TextInput
                  helperText={`Available replacements: ${availableMessageTemplates.join(', ')}`}
                  label="Message"
                  source="message"
                  fullWidth
                  validate={required()}
                />
                <SelectInput
                  source="target_group_id"
                  optionText="name"
                  choices={targetGroupsList}
                  fullWidth
                  validate={required()}
                />
                <BooleanInput label="Send Push Notification" source="send_push_notification" defaultValue={true} />
              </DialogContent>
              <DialogActions>
                <>
                  <Button
                    label="Schedule"
                    type="button"
                    alignIcon="right"
                    size="medium"
                    variant="contained"
                    disabled={
                      submitting ||
                      pristine ||
                      !form.getFieldState('target_group_id')?.value ||
                      !form.getFieldState('message')?.value
                    }
                    onClick={handleOpenInnerModal}
                  >
                    <SendIcon />
                  </Button>
                  <Dialog open={isInnerOpen} onClick={(e) => e.stopPropagation()}>
                    <>
                      <DialogContent>
                        <CustomDateTimeInput label="Select the schedule date" source="schedule_date" />
                      </DialogContent>
                      <DialogActions style={{ justifyContent: 'center' }}>
                        <Button label="ra.action.cancel" onClick={handleCloseInnerModal}>
                          <IconCancel />
                        </Button>

                        <Button
                          disabled={!form.getFieldState('schedule_date')?.value}
                          color="primary"
                          label="Schedule"
                          onClick={() =>
                            handleSchedule({
                              target_group_id: form.getFieldState('target_group_id')?.value,
                              message: form.getFieldState('message')?.value,
                              caption: form.getFieldState('caption')?.value,
                              schedule_date: form.getFieldState('schedule_date')?.value,
                              send_push_notification: form.getFieldState('send_push_notification')?.value,
                            })
                          }
                        >
                          <SendIcon />
                        </Button>
                      </DialogActions>
                    </>
                  </Dialog>
                </>
                <SaveButton
                  disabled={
                    submitting ||
                    pristine ||
                    !form.getFieldState('target_group_id')?.value ||
                    !form.getFieldState('message')?.value
                  }
                  handleSubmitWithRedirect={handleSubmitWithRedirect}
                />
                <Button label="ra.action.cancel" onClick={handleCloseModal}>
                  <IconCancel />
                </Button>
              </DialogActions>
            </>
          )}
        />
      </Dialog>
    </>
  );
};

export default SendToGroupOfUsersWithSchedule;
